import React from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { SegmentedControlProps } from './SegmentedControl.types';

/** Same teal as product drawer step "Precios" (`tealMedium`). */
const TEAL = '#14b8a6';

/**
 * Modern segmented control: no track — only the selected option shows a teal pill.
 * Supports an unset (`null`) value and a compact error outline.
 */
export function SegmentedControl<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  fullWidth = true,
  disabled = false,
  size = 'small',
  error = false,
}: SegmentedControlProps<T>) {
  const isSmall = size === 'small';

  return (
    <Box
      role="radiogroup"
      aria-label={ariaLabel}
      aria-invalid={error || undefined}
      sx={(theme) => ({
        display: 'inline-flex',
        width: fullWidth ? '100%' : 'auto',
        gap: 0.5,
        p: error ? 0.5 : 0,
        borderRadius: error ? 2 : 0,
        backgroundColor: error
          ? alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.12 : 0.06)
          : 'transparent',
        border: error
          ? `1px solid ${alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.55 : 0.45)}`
          : 'none',
        transition: theme.transitions.create(['background-color', 'border-color', 'padding'], {
          duration: 160,
        }),
      })}
    >
      {options.map((option) => {
        const selected = value != null && option.value === value;
        return (
          <ButtonBase
            key={option.value}
            disableRipple
            disabled={disabled}
            aria-pressed={selected}
            role="radio"
            aria-checked={selected}
            aria-label={option.ariaLabel ?? option.label}
            onClick={() => {
              if (!selected) onChange(option.value);
            }}
            sx={(theme) => ({
              flex: fullWidth ? 1 : undefined,
              minWidth: fullWidth ? 0 : undefined,
              px: isSmall ? 1.25 : 1.75,
              py: isSmall ? 0.7 : 0.9,
              borderRadius: 999,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: isSmall ? '0.8125rem' : '0.875rem',
              lineHeight: 1.25,
              whiteSpace: 'normal',
              textAlign: 'center',
              color: selected
                ? theme.palette.mode === 'dark'
                  ? theme.palette.success.light
                  : TEAL
                : theme.palette.text.secondary,
              backgroundColor: selected
                ? theme.palette.mode === 'dark'
                  ? alpha(TEAL, 0.15)
                  : alpha(TEAL, 0.1)
                : 'transparent',
              boxShadow: selected
                ? `inset 0 0 0 1px ${alpha(TEAL, theme.palette.mode === 'dark' ? 0.3 : 0.2)}`
                : 'none',
              transition: theme.transitions.create(['background-color', 'color', 'box-shadow'], {
                duration: 160,
              }),
              '&:hover': {
                backgroundColor: selected
                  ? theme.palette.mode === 'dark'
                    ? alpha(TEAL, 0.22)
                    : alpha(TEAL, 0.14)
                  : theme.palette.mode === 'dark'
                    ? alpha(theme.palette.common.white, 0.06)
                    : alpha(theme.palette.common.black, 0.04),
              },
              '&.Mui-disabled': {
                opacity: 0.5,
              },
            })}
          >
            <Typography
              component="span"
              variant="inherit"
              sx={{
                fontWeight: 'inherit',
                fontSize: 'inherit',
                lineHeight: 'inherit',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {option.label}
            </Typography>
          </ButtonBase>
        );
      })}
    </Box>
  );
}
